<template>
  <div id="scale-weighings-list" class="data-list-container">
    <div class="vx-card p-6">
      <div class="flex flex-wrap items-center justify-between mb-6">
        <h1 class="text-2xl font-bold text-gray-800"></h1>
        <div class="flex flex-wrap items-center gap-3">
          <div class="flex items-center gap-2">
            <label class="text-sm text-gray-500 whitespace-nowrap">From</label>
            <input type="date" v-model="matchDateFrom" class="match-date-input" />
            <label class="text-sm text-gray-500 whitespace-nowrap">To</label>
            <input type="date" v-model="matchDateTo" class="match-date-input" />
          </div>
          <vs-button
            color="warning"
            icon-pack="feather"
            icon="icon-link-2"
            @click="openMatchPopup">
            Match Weighings
          </vs-button>
          <vs-button
            color="success"
            icon-pack="feather"
            icon="icon-download"
            :disabled="!scaleData.length"
            @click="exportExcel">
            Export XLSX
          </vs-button>
          <vs-button
            color="primary"
            icon-pack="feather"
            icon="icon-refresh-cw"
            @click="fetchScaleData">
            Refresh
          </vs-button>
        </div>
      </div>

      <div v-if="loading" class="flex justify-center py-12">
        <vs-progress indeterminate color="primary" />
      </div>

      <vs-table
        v-else
        ref="table"
        pagination
        :max-items="itemsPerPage"
        search
        :data="scaleData">
        <template slot="header">
          <div class="flex flex-wrap items-center gap-4 mb-4">
            <vs-dropdown vs-trigger-click class="cursor-pointer">
              <div class="p-3 border border-solid d-theme-border-grey-light rounded-lg d-theme-dark-bg cursor-pointer flex items-center font-medium">
                <span class="mr-2">{{ itemsPerPage }} per page</span>
                <feather-icon icon="ChevronDownIcon" svgClasses="h-4 w-4" />
              </div>
              <vs-dropdown-menu>
                <vs-dropdown-item @click="itemsPerPage = 10"><span>10</span></vs-dropdown-item>
                <vs-dropdown-item @click="itemsPerPage = 25"><span>25</span></vs-dropdown-item>
                <vs-dropdown-item @click="itemsPerPage = 50"><span>50</span></vs-dropdown-item>
                <vs-dropdown-item @click="itemsPerPage = 100"><span>100</span></vs-dropdown-item>
              </vs-dropdown-menu>
            </vs-dropdown>
            <span class="text-sm text-gray-500">{{ scaleData.length }} record(s)</span>
          </div>
        </template>

        <template slot="thead">
          <vs-th sort-key="date">Date</vs-th>
          <vs-th sort-key="scale_id">Scale</vs-th>
          <vs-th sort-key="plaque">Plate</vs-th>
          <vs-th sort-key="plaque_trailer">Trailer</vs-th>
          <vs-th sort-key="motif">Motif</vs-th>
          <vs-th sort-key="kg_a"><span class="block text-right">Kg A</span></vs-th>
          <vs-th sort-key="kg_b"><span class="block text-right">Kg B</span></vs-th>
          <vs-th sort-key="kg_net"><span class="block text-right">Kg net</span></vs-th>
          <vs-th sort-key="idoperation">Operation</vs-th>
        </template>

        <template slot-scope="{ data }">
          <tbody>
            <vs-tr :data="tr" :key="tr.id || idx" v-for="(tr, idx) in data">
              <vs-td>
                <span class="font-medium">{{ formatDate(tr.date) }}</span>
              </vs-td>
              <vs-td>
                <vs-chip color="primary" class="font-mono">{{ tr.scale_id }}</vs-chip>
              </vs-td>
              <vs-td><span>{{ tr.plaque || '-' }}</span></vs-td>
              <vs-td><span>{{ tr.plaque_trailer || '-' }}</span></vs-td>
              <vs-td><span>{{ tr.motif || '-' }}</span></vs-td>
              <vs-td class="text-right"><span>{{ tr.kg_a | formatWeight }}</span></vs-td>
              <vs-td class="text-right"><span>{{ tr.kg_b | formatWeight }}</span></vs-td>
              <vs-td class="text-right">
                <span class="font-semibold text-primary">{{ tr.kg_net | formatWeight }}</span>
              </vs-td>
              <vs-td>
                <router-link
                  v-if="tr.idoperation"
                  :to="operationLink(tr)"
                  class="text-primary hover:underline">
                  #{{ tr.idoperation }}
                </router-link>
                <span v-else class="text-gray-400">-</span>
              </vs-td>
            </vs-tr>
          </tbody>
        </template>
      </vs-table>

      <div v-if="!loading && scaleData.length === 0" class="text-center py-16 text-gray-500">
        <feather-icon icon="BarChart2Icon" class="w-16 h-16 mx-auto mb-4 opacity-50" />
        <p class="text-lg">No weighings recorded</p>
      </div>
    </div>

    <!-- ─── MATCH POPUP ─────────────────────────────────────────────── -->
    <vs-popup
      class="match-popup"
      title="Match Weighings to Operations"
      :active.sync="showMatchPopup">

      <!-- Toolbar -->
      <div class="popup-toolbar">
        <div class="flex items-center gap-2 flex-wrap">
          <label class="text-sm text-gray-500">From</label>
          <input type="date" v-model="matchDateFrom" class="match-date-input" />
          <label class="text-sm text-gray-500">To</label>
          <input type="date" v-model="matchDateTo" class="match-date-input" />
          <vs-button size="small" color="primary" icon-pack="feather" icon="icon-search" @click="autoMode ? runAutoMatch() : loadPopupData()">
            Load
          </vs-button>
          <vs-button
            v-if="!autoMode"
            size="small"
            color="warning"
            icon-pack="feather"
            icon="icon-zap"
            @click="runAutoMatch">
            Auto-match
          </vs-button>
          <vs-button
            v-else
            size="small"
            color="dark"
            type="border"
            icon-pack="feather"
            icon="icon-mouse-pointer"
            @click="autoMode = false; loadPopupData()">
            Manual
          </vs-button>
          <vs-button
            v-if="!autoMode"
            size="small"
            :color="showSynced ? 'success' : 'dark'"
            type="border"
            icon-pack="feather"
            :icon="showSynced ? 'icon-eye-off' : 'icon-eye'"
            @click="showSynced = !showSynced">
            {{ showSynced ? 'Hide synced' : 'Show synced' }}
          </vs-button>
        </div>
        <div v-if="autoMode && autoResult" class="link-bar">
          <span class="link-bar-label">
            <feather-icon icon="ZapIcon" svgClasses="w-4 h-4" />
            {{ selectedGroupIds.length }} / {{ autoResult.groups.length }} weighing(s) selected
          </span>
          <vs-button size="small" color="dark" type="border" @click="selectGroups('high')">High only</vs-button>
          <vs-button size="small" color="dark" type="border" @click="selectGroups('all')">All</vs-button>
          <vs-button size="small" color="dark" type="border" @click="selectGroups('none')">None</vs-button>
          <vs-button size="small" color="success" icon-pack="feather" icon="icon-check"
            :disabled="applyLoading || selectedGroupIds.length === 0" @click="applyAutoMatch">
            {{ applyLoading ? 'Applying…' : `Apply (${selectedGroupIds.length})` }}
          </vs-button>
        </div>
        <div v-else-if="!autoMode && selectedAvizList.length > 0 && selectedWeighing" class="link-bar">
          <!-- Single aviz -->
          <span v-if="selectedAvizList.length === 1" class="link-bar-label">
            <feather-icon icon="LinkIcon" svgClasses="w-4 h-4" />
            Op <strong>#{{ selectedAvizList[0].id }}</strong>
            &nbsp;↔&nbsp;
            Weighing <strong>#{{ selectedWeighing.id }}</strong>
            &nbsp;·&nbsp;
            <strong class="text-success">{{ (kgOf(selectedWeighing) / 1000).toFixed(3) }} t</strong>
            <span v-if="selectedAvizList[0].transport_tonaj" class="overwrite-warning">
              <feather-icon icon="AlertTriangleIcon" svgClasses="w-3.5 h-3.5" />
              Overwrites {{ selectedAvizList[0].transport_tonaj }} t
            </span>
          </span>
          <!-- Several aviz — show m3 split preview -->
          <span v-else class="link-bar-label link-bar-split">
            <feather-icon icon="LinkIcon" svgClasses="w-4 h-4" />
            <span v-for="(s, i) in splitPreview" :key="s.id">
              <template v-if="i > 0">&nbsp;+&nbsp;</template>
              Op <strong>#{{ s.id }}</strong>
              <span class="split-detail">({{ s.m3 }} m³ → <strong class="text-success">{{ s.tonnes }} t</strong>)</span>
            </span>
            &nbsp;↔&nbsp;Weighing <strong>#{{ selectedWeighing.id }}</strong>
            &nbsp;·&nbsp;<strong>{{ (kgOf(selectedWeighing) / 1000).toFixed(3) }} t total</strong>
            <span v-if="selectedAvizList.some(a => a.transport_tonaj)" class="overwrite-warning">
              <feather-icon icon="AlertTriangleIcon" svgClasses="w-3.5 h-3.5" />
              Overwrites existing
            </span>
          </span>
          <input
            v-if="needsPassword"
            v-model="overridePassword"
            type="password"
            placeholder="Password"
            class="override-password-input"
            @keyup.enter="linkSelected" />
          <vs-button size="small" color="success" icon-pack="feather" icon="icon-check" @click="linkSelected" :disabled="linkLoading">
            {{ linkLoading ? 'Linking…' : 'Link' }}
          </vs-button>
        </div>
      </div>

      <!-- Hint when nothing selected -->
      <template v-if="!autoMode">
        <p v-if="selectedAvizList.length === 0 && !selectedWeighing" class="text-sm text-gray-400 px-1 mb-3">
          Click one or more operations on the left, then a weighing on the right to link them.
        </p>
        <p v-else-if="selectedAvizList.length > 0 && !selectedWeighing" class="text-sm text-warning px-1 mb-3">
          Now select a weighing on the right →
        </p>
        <p v-else-if="selectedAvizList.length === 0 && selectedWeighing" class="text-sm text-warning px-1 mb-3">
          ← Now select one or more operations on the left
        </p>
      </template>
      <p v-else class="text-sm text-gray-400 px-1 mb-3">
        Same day, shared plate (truck or trailer), first weighing after the aviz was issued. Several avize on one weighing share its weight by m³. Times are Romanian time.
      </p>

      <div v-if="popupLoading" class="flex justify-center py-12">
        <vs-progress indeterminate color="primary" />
      </div>

      <!-- AUTO-MATCH proposal -->
      <div v-else-if="autoMode && autoResult" class="auto-body">
        <div v-if="autoResult.groups.length === 0" class="match-empty">Nothing to match in this range</div>
        <div
          v-for="g in autoResult.groups"
          :key="g.weighing.id"
          class="auto-group"
          :class="{ 'auto-group--off': !selectedGroupIds.includes(g.weighing.id), 'auto-group--low': g.confidence === 'low' }"
          @click="toggleGroup(g)">
          <div class="auto-group-check">
            <input type="checkbox" class="auto-check" :checked="selectedGroupIds.includes(g.weighing.id)" @click.stop="toggleGroup(g)" />
          </div>
          <div class="auto-group-weighing">
            <div class="match-item-main">
              <span class="match-item-plate">{{ g.weighing.plaque }}</span>
              <span v-if="g.weighing.plaque_trailer" class="match-item-trailer">/ {{ g.weighing.plaque_trailer }}</span>
            </div>
            <div class="match-item-sub">{{ formatDateRo(g.weighing.date) }} · #{{ g.weighing.id }}</div>
            <div class="auto-kg">{{ Number(g.weighing.kg_used).toLocaleString('fr-FR') }} kg</div>
            <div v-if="g.weighing.tare_estimated" class="match-item-sub">
              recorded net {{ Number(g.weighing.kg_net).toLocaleString('fr-FR') }} kg
            </div>
          </div>
          <div class="auto-group-arrow">←</div>
          <div class="auto-group-ops">
            <div v-for="o in g.operations" :key="o.id" class="auto-op">
              <span class="match-item-id">#{{ o.id }}</span>
              <span class="match-item-plate">{{ o.truck_plate }}</span>
              <span class="text-gray-400">{{ o.name }}</span>
              <span class="auto-op-time" :title="o.sumal_aviz_nr">aviz {{ formatTimeRo(o.aviz_time) }}</span>
              <span class="match-item-m3">{{ o.total_m3 }} m³</span>
              <strong class="text-success auto-op-t">{{ o.tonnes }} t</strong>
            </div>
          </div>
          <div class="auto-group-meta">
            <span class="auto-chip" :class="g.confidence === 'high' ? 'auto-chip--high' : 'auto-chip--low'">{{ g.confidence }}</span>
            <span v-if="g.density" class="auto-density">{{ g.density }} kg/m³</span>
            <span v-for="wn in g.warnings" :key="wn" class="auto-warning">{{ warningLabel(wn, g) }}</span>
          </div>
        </div>

        <div v-if="autoResult.unmatched_operations.length" class="auto-unmatched">
          <div class="auto-unmatched-title" @click="showUnmatched = !showUnmatched">
            <feather-icon :icon="showUnmatched ? 'ChevronDownIcon' : 'ChevronRightIcon'" svgClasses="w-4 h-4" />
            {{ autoResult.unmatched_operations.length }} aviz(e) without weighing
          </div>
          <template v-if="showUnmatched">
            <div v-for="o in autoResult.unmatched_operations" :key="o.id" class="auto-op auto-op--unmatched">
              <span class="match-item-id">#{{ o.id }}</span>
              <span class="match-item-plate">{{ o.truck_plate }}</span>
              <span class="text-gray-400">{{ o.name }}</span>
              <span>{{ formatDateShort(o.date) }}</span>
              <span class="auto-op-time">aviz {{ formatTimeRo(o.aviz_time) }}</span>
              <span class="match-item-m3">{{ o.total_m3 }} m³</span>
              <span class="auto-warning">{{ reasonLabel(o) }}</span>
            </div>
          </template>
        </div>
        <div v-if="autoResult.unmatched_weighings.length" class="auto-unmatched">
          <div class="auto-unmatched-title" @click="showUnmatchedW = !showUnmatchedW">
            <feather-icon :icon="showUnmatchedW ? 'ChevronDownIcon' : 'ChevronRightIcon'" svgClasses="w-4 h-4" />
            {{ autoResult.unmatched_weighings.length }} forest weighing(s) without aviz
          </div>
          <template v-if="showUnmatchedW">
            <div v-for="w in autoResult.unmatched_weighings" :key="w.id" class="auto-op auto-op--unmatched">
              <span class="match-item-plate">{{ w.plaque }}</span>
              <span v-if="w.plaque_trailer" class="match-item-trailer">/ {{ w.plaque_trailer }}</span>
              <span>{{ formatDateRo(w.date) }}</span>
              <span class="match-item-kg">{{ Number(w.kg_net).toLocaleString('fr-FR') }} kg</span>
            </div>
          </template>
        </div>
      </div>

      <!-- MANUAL: two columns -->
      <div v-else class="match-columns">

        <!-- LEFT: Aviz / Operations -->
        <div class="match-col">
          <div class="match-col-header">
            <feather-icon icon="FileTextIcon" svgClasses="w-4 h-4" />
            <span>Aviz / Operations</span>
            <span class="match-col-count">{{ filteredAvizList.length }}</span>
          </div>
          <div class="match-col-body">
            <div v-if="filteredAvizList.length === 0" class="match-empty">No operations found</div>
            <div
              v-for="op in filteredAvizList"
              :key="op.id"
              class="match-item"
              :class="{
                'match-item--selected':  selectedAvizList.some(a => a.id === op.id),
                'match-item--linked':    op.transport_tonaj,
                'match-item--suggested': selectedWeighing && platesSuggest(selectedWeighing, op),
              }"
              @click="selectAviz(op)">
              <div class="match-item-main">
                <span
                  v-if="selectedAvizList.some(a => a.id === op.id)"
                  class="match-item-sel-badge">
                  {{ selectedAvizList.findIndex(a => a.id === op.id) + 1 }}
                </span>
                <span class="match-item-id">#{{ op.id }}</span>
                <span class="match-item-plate">{{ op.truck_plate }}</span>
                <span v-if="op.trailer_plate" class="match-item-trailer">/ {{ op.trailer_plate }}</span>
                <span v-if="op.total_m3 > 0" class="match-item-m3">{{ op.total_m3 }} m³</span>
              </div>
              <div class="match-item-sub">
                <span>{{ formatDateShort(op.date) }}</span>
                <span v-if="op.sumal_aviz_nr" class="match-item-aviz-nr">{{ op.sumal_aviz_nr }}</span>
                <span v-if="op.name" class="text-gray-400 ml-2 truncate">{{ op.name }}</span>
              </div>
              <div v-if="op.transport_tonaj" class="match-item-badge linked">
                <feather-icon icon="CheckCircleIcon" svgClasses="w-3 h-3" />
                {{ op.transport_tonaj }} t
              </div>
            </div>
          </div>
        </div>

        <!-- RIGHT: Weighings -->
        <div class="match-col">
          <div class="match-col-header">
            <feather-icon icon="BarChart2Icon" svgClasses="w-4 h-4" />
            <span>Weighings / Pesées</span>
            <span class="match-col-count">{{ filteredWeighings.length }}</span>
          </div>
          <div class="match-col-body">
            <div v-if="filteredWeighings.length === 0" class="match-empty">No weighings found</div>
            <div
              v-for="w in filteredWeighings"
              :key="w.id"
              class="match-item"
              :class="{
                'match-item--selected':  selectedWeighing && selectedWeighing.id === w.id,
                'match-item--linked':    w.idoperation,
                'match-item--suggested': selectedAvizList.length > 0 && selectedAvizList.some(a => platesSuggest(w, a)),
              }"
              @click="selectWeighing(w)">
              <div class="match-item-main">
                <span class="match-item-plate">{{ w.plaque }}</span>
                <span v-if="w.plaque_trailer" class="match-item-trailer">/ {{ w.plaque_trailer }}</span>
                <span class="match-item-kg">{{ kgOf(w).toLocaleString('fr-FR') }} kg</span>
              </div>
              <div class="match-item-sub">
                <span>{{ formatDate(w.date) }}</span>
              </div>
              <div v-if="w.tare_estimated" class="auto-warning mt-1 inline-block" :title="`Gross ${w.kg_a} − usual tare ${w.tare_usual}`">
                Tare taken loaded ({{ Number(w.kg_b).toLocaleString('fr-FR') }} kg, recorded net {{ Number(w.kg_net).toLocaleString('fr-FR') }}) → gross − usual tare {{ Number(w.tare_usual).toLocaleString('fr-FR') }}
              </div>
              <div v-if="w.idoperation" class="match-item-badge linked">
                <feather-icon icon="LinkIcon" svgClasses="w-3 h-3" />
                Op #{{ w.idoperation }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </vs-popup>
  </div>
</template>

<script>
import axios from "@/axios.js";
import * as XLSX from "xlsx";

export default {
  name: "ScaleWeighingsList",
  filters: {
    formatWeight(value) {
      if (value == null || value === '') return '-';
      const parsed = Number(value);
      if (Number.isNaN(parsed)) return '-';
      return Math.round(parsed).toLocaleString('fr-FR');
    }
  },
  data() {
    const today = new Date().toISOString().slice(0, 10);
    return {
      scaleData: [],
      loading: true,
      itemsPerPage: 25,
      // popup state
      showMatchPopup: false,
      matchDateFrom: today,
      matchDateTo: today,
      popupLoading: false,
      avizList: [],
      popupWeighings: [],
      selectedAvizList: [],
      selectedWeighing: null,
      linkLoading: false,
      overridePassword: "",
      showSynced: false,
      // auto-match
      autoMode: false,
      autoResult: null,
      selectedGroupIds: [],
      applyLoading: false,
      showUnmatched: false,
      showUnmatchedW: false,
    };
  },
  computed: {
    needsPassword() {
      return this.selectedAvizList.some(op => op.transport_tonaj && op.transport_tonaj !== "");
    },

    // Plates from aviz that are not yet synced — used to filter the weighings column
    pendingAvizPlates() {
      const norm = (s) => (s || '').toUpperCase().replace(/\s+/g, '').split('/').filter(Boolean);
      const plates = new Set();
      this.avizList
        .filter(op => !op.transport_tonaj || op.transport_tonaj === '')
        .forEach(op => {
          norm(op.truck_plate).forEach(p => plates.add(p));
          norm(op.trailer_plate).forEach(p => plates.add(p));
        });
      return plates;
    },

    filteredAvizList() {
      if (this.showSynced) return this.avizList;
      return this.avizList.filter(op => !op.transport_tonaj || op.transport_tonaj === '');
    },

    filteredWeighings() {
      const norm = (s) => (s || '').toUpperCase().replace(/\s+/g, '').split('/').filter(Boolean);
      if (this.showSynced) return this.popupWeighings;
      return this.popupWeighings.filter(w => {
        if (w.idoperation) return false;
        const wPlates = [...norm(w.plaque), ...norm(w.plaque_trailer)];
        return wPlates.some(p => this.pendingAvizPlates.has(p));
      });
    },

    splitPreview() {
      if (this.selectedAvizList.length < 2 || !this.selectedWeighing) return [];
      const totalM3 = this.selectedAvizList.reduce((s, op) => s + Number(op.total_m3 || 0), 0);
      const totalT  = this.kgOf(this.selectedWeighing) / 1000;
      return this.selectedAvizList.map(op => {
        const ratio  = totalM3 > 0 ? Number(op.total_m3 || 0) / totalM3 : 1 / this.selectedAvizList.length;
        return { id: op.id, tonnes: (totalT * ratio).toFixed(3), m3: op.total_m3 || 0 };
      });
    },
  },
  mounted() {
    this.fetchScaleData();
  },
  methods: {
    async fetchScaleData() {
      this.loading = true;
      try {
        const res = await axios.get("/scale/data");
        this.scaleData = Array.isArray(res.data) ? res.data : [];
      } catch (e) {
        console.error("Scale data error:", e);
        this.$vs.notify({ title: "Error", text: "Failed to load weighings", color: "danger" });
        this.scaleData = [];
      } finally {
        this.loading = false;
      }
    },

    async openMatchPopup() {
      this.showMatchPopup = true;
      this.autoMode = false;
      this.selectedAvizList = [];
      this.selectedWeighing = null;
      await this.loadPopupData();
    },

    async loadPopupData() {
      this.popupLoading = true;
      this.selectedAvizList = [];
      this.selectedWeighing = null;
      try {
        const [avizRes, weighRes] = await Promise.all([
          axios.get(`/scale/aviz?date_from=${this.matchDateFrom}&date_to=${this.matchDateTo}`),
          // Days are Romanian days, filtered by the API
          axios.get(`/scale/data?date_from=${this.matchDateFrom}&date_to=${this.matchDateTo}`),
        ]);
        this.avizList = Array.isArray(avizRes.data) ? avizRes.data : [];
        this.popupWeighings = (Array.isArray(weighRes.data) ? weighRes.data : [])
          .sort((a, b) => new Date(a.date) - new Date(b.date));
      } catch (e) {
        console.error("Popup load error:", e);
        this.$vs.notify({ title: "Error", text: "Failed to load data", color: "danger" });
      } finally {
        this.popupLoading = false;
      }
    },

    selectAviz(op) {
      const idx = this.selectedAvizList.findIndex(a => a.id === op.id);
      if (idx !== -1) {
        // Already selected → deselect
        this.selectedAvizList.splice(idx, 1);
      } else {
        // One weighing can cover several avize
        this.selectedAvizList.push(op);
      }
      this.overridePassword = "";
    },

    selectWeighing(w) {
      this.selectedWeighing = this.selectedWeighing && this.selectedWeighing.id === w.id ? null : w;
    },

    /** Returns true if the weighing's plates overlap with the operation's plates */
    platesSuggest(weighing, op) {
      const normalize = (s) => (s || '').toUpperCase().replace(/\s+/g, '').split('/').filter(Boolean);
      const wPlates  = [...normalize(weighing.plaque), ...normalize(weighing.plaque_trailer)];
      const opPlates = [...normalize(op.truck_plate),  ...normalize(op.trailer_plate)];
      return wPlates.some(p => opPlates.includes(p));
    },

    async linkSelected() {
      if (this.selectedAvizList.length === 0 || !this.selectedWeighing) return;
      this.linkLoading = true;
      try {
        const body = {
          weighing_id:   this.selectedWeighing.id,
          operation_ids: this.selectedAvizList.map(op => op.id),
        };
        if (this.needsPassword) body.password = this.overridePassword;

        const res = await axios.post("/scale/link", body);

        // Update local state from API response
        if (res.data && res.data.results) {
          res.data.results.forEach(r => {
            const op = this.avizList.find(a => a.id === r.operation_id);
            if (op) op.transport_tonaj = r.tonnes;
          });
        }
        this.selectedWeighing.idoperation = Math.min(...this.selectedAvizList.map(op => op.id));

        const ids = this.selectedAvizList.map(op => `#${op.id}`).join(' + ');
        this.$vs.notify({
          title: "Linked",
          text: `Op ${ids} ↔ Weighing #${this.selectedWeighing.id}`,
          color: "success"
        });
        this.selectedAvizList = [];
        this.selectedWeighing = null;
        // Refresh main table in background
        this.fetchScaleData();
      } catch (e) {
        if (e.response && e.response.status === 403) {
          this.$vs.notify({ title: "Wrong password", text: "Incorrect password to overwrite existing value", color: "danger" });
        } else {
          console.error("Link error:", e);
          this.$vs.notify({ title: "Error", text: "Failed to link", color: "danger" });
        }
      } finally {
        this.linkLoading = false;
      }
    },

    async runAutoMatch() {
      this.autoMode = true;
      this.popupLoading = true;
      this.autoResult = null;
      try {
        const res = await axios.post("/scale/match-operations", {
          date_from: this.matchDateFrom,
          date_to: this.matchDateTo,
        });
        this.autoResult = res.data;
        this.selectGroups('high');
      } catch (e) {
        console.error("Auto-match error:", e);
        const msg = (e.response && e.response.data && e.response.data.error) || "Auto-match failed";
        this.$vs.notify({ title: "Error", text: msg, color: "danger" });
        this.autoMode = false;
      } finally {
        this.popupLoading = false;
      }
    },

    selectGroups(which) {
      const groups = (this.autoResult && this.autoResult.groups) || [];
      this.selectedGroupIds = groups
        .filter(g => which === 'all' || (which === 'high' && g.confidence === 'high'))
        .map(g => g.weighing.id);
    },

    toggleGroup(g) {
      const id = g.weighing.id;
      const i = this.selectedGroupIds.indexOf(id);
      if (i === -1) this.selectedGroupIds.push(id);
      else this.selectedGroupIds.splice(i, 1);
    },

    async applyAutoMatch() {
      const groups = this.autoResult.groups
        .filter(g => this.selectedGroupIds.includes(g.weighing.id))
        .map(g => ({ weighing_id: g.weighing.id, operation_ids: g.operations.map(o => o.id) }));
      if (!groups.length) return;
      this.applyLoading = true;
      try {
        const res = await axios.post("/scale/match-apply", { groups });
        const { applied_count, skipped } = res.data;
        this.$vs.notify({
          title: "Auto-match applied",
          text: `${applied_count} weighing(s) linked` + (skipped.length ? `, ${skipped.length} skipped (${[...new Set(skipped.map(s => s.reason))].join(', ')})` : ''),
          color: skipped.length ? "warning" : "success",
          time: 6000,
        });
        this.fetchScaleData();
        await this.runAutoMatch();
      } catch (e) {
        console.error("Apply error:", e);
        this.$vs.notify({ title: "Error", text: "Failed to apply", color: "danger" });
      } finally {
        this.applyLoading = false;
      }
    },

    warningLabel(code) {
      if (code === 'density_out_of_range') return 'Unusual density';
      if (code === 'long_gap') return 'Aviz issued > 14 h before';
      if (code === 'no_volume') return 'No m³ on aviz';
      if (code === 'no_aviz_time') return 'Aviz time unreadable';
      if (code === 'tare_estimated') return 'Tare taken loaded → gross − usual tare';
      if (code.startsWith('motif_')) return `Motif: ${code.slice(6)}`;
      return code;
    },

    reasonLabel(o) {
      switch (o.reason) {
        case 'no_weighing_same_day_same_plate': return 'No weighing that day with this plate';
        case 'no_weighing_after_aviz_time': return 'No weighing after the aviz time';
        case 'weighing_already_linked': return `Weighing #${o.weighing_id} already linked to op #${o.linked_operation_id}`;
        case 'all_weighings_taken': return 'All weighings of that day already taken';
        case 'later_trip_before_weighing': return `Truck loaded again (op #${o.later_operation_id}) before weighing #${o.weighing_id}: this aviz's weighing is missing`;
        default: return o.reason;
      }
    },

    /** Net retained: corrected when the tare was taken with the truck still loaded. */
    kgOf(w) {
      return Number(w.kg_used != null ? w.kg_used : w.kg_net) || 0;
    },

    formatDateRo(d) {
      if (!d) return '-';
      return new Date(d).toLocaleString('fr-FR', {
        timeZone: 'Europe/Bucharest',
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    },

    formatTimeRo(d) {
      if (!d) return '?';
      return new Date(d).toLocaleString('fr-FR', {
        timeZone: 'Europe/Bucharest', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
      });
    },

    exportExcel() {
      if (!this.scaleData.length) {
        this.$vs.notify({ title: "Warning", text: "No data to export", color: "warning" });
        return;
      }
      const rows = this.scaleData.map(tr => ({
        ID: tr.id,
        Date: this.formatDate(tr.date),
        Scale: tr.scale_id,
        Plate: tr.plaque || "",
        Trailer: tr.plaque_trailer || "",
        Motif: tr.motif || "",
        "Kg A": tr.kg_a != null && tr.kg_a !== "" ? Number(tr.kg_a) : "",
        "Kg B": tr.kg_b != null && tr.kg_b !== "" ? Number(tr.kg_b) : "",
        "Kg net": tr.kg_net != null && tr.kg_net !== "" ? Number(tr.kg_net) : "",
        Operation: tr.idoperation || "",
      }));
      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Weighings");
      const fileName = `weighings_export_${new Date().toISOString().split("T")[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);
    },

    /**
     * Build the link for a weighing's linked operation, depending on its category:
     *   reception (idoperationcategory 1)            → /reception/:id
     *   harvesting / production (idoperationcategory 3) → /production/:id
     *   anything else                                → /invoice/operation-edit/:id
     */
    operationLink(tr) {
      const cat = tr.operation ? Number(tr.operation.idoperationcategory) : null;
      if (cat === 1) return `/reception/${tr.idoperation}`;
      if (cat === 3) return `/production/${tr.idoperation}`;
      return `/invoice/operation-edit/${tr.idoperation}`;
    },

    formatDate(d) {
      if (!d) return '-';
      return new Date(d).toLocaleString('fr-FR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    },

    formatDateShort(d) {
      if (!d) return '-';
      return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    },
  }
};
</script>

<style scoped>
.vx-card {
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.06);
}

/* Force right alignment for kg columns */
#scale-weighings-list ::v-deep th:nth-child(6),
#scale-weighings-list ::v-deep th:nth-child(7),
#scale-weighings-list ::v-deep th:nth-child(8),
#scale-weighings-list ::v-deep td:nth-child(6),
#scale-weighings-list ::v-deep td:nth-child(7),
#scale-weighings-list ::v-deep td:nth-child(8) {
  text-align: right !important;
}
#scale-weighings-list ::v-deep th:nth-child(6) .vs-table-text,
#scale-weighings-list ::v-deep th:nth-child(7) .vs-table-text,
#scale-weighings-list ::v-deep th:nth-child(8) .vs-table-text {
  justify-content: flex-end !important;
}

.match-date-input {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 13px;
  color: #333;
  background: #fff;
  outline: none;
}
.match-date-input:focus { border-color: #7367f0; }

/* ─── POPUP ─────────────────────────────────── */
.match-popup ::v-deep .vs-popup {
  width: 90vw !important;
  max-width: 1200px;
}
.match-popup ::v-deep .vs-popup--content {
  padding: 12px 20px 20px;
  overflow: hidden;
}

.popup-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
}

.link-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #f0faf5;
  border: 1px solid #28c76f44;
  border-radius: 8px;
  padding: 6px 14px;
}
.link-bar-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
}
.overwrite-warning {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #ff9f43;
  font-size: 12px;
  font-weight: 600;
}
.override-password-input {
  border: 1.5px solid #ff9f43;
  border-radius: 6px;
  padding: 5px 10px;
  font-size: 13px;
  width: 110px;
  outline: none;
  background: #fff;
}
.override-password-input:focus {
  border-color: #e08020;
}

/* Two-column layout */
.match-columns {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  height: 60vh;
}

.match-col {
  display: flex;
  flex-direction: column;
  border: 1px solid #e8e8e8;
  border-radius: 10px;
  overflow: hidden;
}

.match-col-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: #f8f8f8;
  font-weight: 600;
  font-size: 13px;
  color: #555;
  border-bottom: 1px solid #e8e8e8;
  flex-shrink: 0;
}
.match-col-count {
  margin-left: auto;
  background: #e0e0e0;
  border-radius: 12px;
  padding: 1px 8px;
  font-size: 11px;
  font-weight: 600;
  color: #666;
}

.match-col-body {
  flex: 1;
  overflow-y: auto;
  padding: 6px;
}

.match-empty {
  padding: 24px;
  text-align: center;
  color: #aaa;
  font-size: 13px;
}

/* Match items */
.match-item {
  position: relative;
  padding: 9px 12px;
  border-radius: 7px;
  margin-bottom: 4px;
  cursor: pointer;
  border: 1.5px solid transparent;
  background: #fff;
  transition: background 0.12s, border-color 0.12s;
}
.match-item:hover {
  background: #f5f5f5;
}
.match-item--selected {
  border-color: #7367f0 !important;
  background: #f3f1ff !important;
}
.match-item--suggested {
  border-color: #ff9f4388 !important;
  background: #fffaf3 !important;
}
.match-item--selected.match-item--suggested {
  border-color: #7367f0 !important;
  background: #f3f1ff !important;
}
.match-item--linked {
  opacity: 0.65;
}

.match-item-main {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
}
.match-item-id {
  font-weight: 700;
  color: #7367f0;
  font-size: 12px;
}
.match-item-plate {
  font-family: monospace;
  font-weight: 600;
  font-size: 13px;
  color: #333;
}
.match-item-trailer {
  font-family: monospace;
  font-size: 12px;
  color: #888;
}
.match-item-sel-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #7367f0;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  flex-shrink: 0;
}
.match-item-m3 {
  margin-left: auto;
  font-size: 11px;
  font-weight: 600;
  color: #00cfe8;
  background: #e0f9fc;
  border-radius: 4px;
  padding: 1px 6px;
}
.link-bar-split {
  flex-wrap: wrap;
  row-gap: 2px;
}
.split-detail {
  font-size: 11px;
  color: #666;
  margin-left: 2px;
}
.match-item-aviz-nr {
  margin-left: 8px;
  font-size: 11px;
  font-weight: 600;
  color: #7367f0;
  background: #f0eeff;
  border-radius: 4px;
  padding: 1px 6px;
}
.match-item-kg {
  margin-left: auto;
  font-weight: 600;
  color: #28c76f;
  font-size: 12px;
}
.match-item-sub {
  font-size: 11px;
  color: #aaa;
  margin-top: 2px;
  display: flex;
  align-items: center;
}

.match-item-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 10px;
  margin-top: 4px;
}
.match-item-badge.linked {
  background: #d4f8e5;
  color: #1a8a4a;
}
.auto-body {
  height: 60vh;
  overflow-y: auto;
  padding-right: 4px;
}
.auto-group {
  display: grid;
  grid-template-columns: 28px 200px 20px 1fr 190px;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  margin-bottom: 6px;
  border: 1.5px solid #e8e8e8;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
}
.auto-group:hover { background: #fafafa; }
.auto-group--low { border-color: #ff9f4366; }
.auto-group--off { opacity: 0.55; }
.auto-check { width: 16px; height: 16px; cursor: pointer; accent-color: #7367f0; }
.auto-group-arrow { color: #bbb; text-align: center; }
.auto-kg { font-weight: 700; color: #28c76f; font-size: 13px; margin-top: 2px; }
.auto-group-ops { display: flex; flex-direction: column; gap: 4px; }
.auto-op {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  flex-wrap: wrap;
}
.auto-op--unmatched { padding: 6px 10px; border-bottom: 1px solid #f3f3f3; }
.auto-op-time { font-size: 11px; color: #7367f0; font-weight: 600; }
.auto-op-t { margin-left: 4px; font-size: 12px; }
.auto-group-meta { display: flex; flex-wrap: wrap; gap: 4px; justify-content: flex-end; align-items: center; }
.auto-chip {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: 10px;
}
.auto-chip--high { background: #d4f8e5; color: #1a8a4a; }
.auto-chip--low { background: #fff1e0; color: #c26a00; }
.auto-density { font-size: 11px; color: #666; }
.auto-warning {
  font-size: 10px;
  font-weight: 600;
  color: #c26a00;
  background: #fff7ec;
  border-radius: 4px;
  padding: 1px 6px;
}
.auto-unmatched {
  margin-top: 14px;
  border: 1px solid #eee;
  border-radius: 8px;
}
.auto-unmatched-title {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  font-weight: 600;
  font-size: 13px;
  color: #555;
  background: #f8f8f8;
  cursor: pointer;
}
</style>
